import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell } from 'lucide-react';
import { cn } from 'lib/utils';
import { Button } from 'components/ui/button';
import { formatNotificationDateTime } from 'lib/dateFormat';
import * as Notifications from 'modules/notifications';

const MAX_ITEMS = 20;

export const NotificationBell: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { items } = Notifications.Hooks.useList({ limit: MAX_ITEMS });
  const { unreadCount } = Notifications.Hooks.useUnreadCount();
  const markRead = Notifications.Hooks.useMarkRead();

  // Close on outside click, or on Escape — which also returns focus to the
  // trigger button, matching the standard disclosure-widget pattern.
  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false);
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setIsOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handleItemClick = (notification: Notifications.Types.INotification) => {
    const rendered = Notifications.renderNotification(notification);
    if (!notification.readAt) {
      markRead.mutate({ ids: [notification.id] });
    }
    setIsOpen(false);
    if (rendered.href) navigate(rendered.href);
  };

  const handleMarkAllRead = () => {
    if (unreadCount === 0) return;
    markRead.mutate({ all: true });
  };

  const displayItems = items.slice(0, MAX_ITEMS);
  const hasUnread = unreadCount > 0;
  const badgeLabel = unreadCount > 99 ? '99+' : String(unreadCount);

  return (
    <div className="relative" ref={ref}>
      <Button
        ref={triggerRef}
        variant="ghost"
        size="icon"
        className="relative"
        onClick={() => setIsOpen((v) => !v)}
        aria-label={t('notifications')}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <Bell size={18} />
        {hasUnread && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-error-600 text-white rounded-full text-[10px] font-medium leading-none flex items-center justify-center border-2 border-background"
            aria-label={t('notifications_unread_count', { count: unreadCount, defaultValue: '{{count}} unread' })}
          >
            {badgeLabel}
          </span>
        )}
      </Button>

      {isOpen && (
        <div
          role="dialog"
          aria-modal="false"
          aria-label={t('notifications')}
          className="absolute right-0 top-full mt-2 z-50 bg-popover border border-border rounded-lg shadow-lg overflow-hidden w-80 sm:w-96"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <h3 className="text-sm font-semibold">{t('notifications')}</h3>
            <button
              type="button"
              onClick={handleMarkAllRead}
              disabled={!hasUnread || markRead.isPending}
              className={cn(
                'text-xs font-medium transition-colors',
                hasUnread
                  ? 'text-foreground hover:underline cursor-pointer'
                  : 'text-muted-foreground cursor-not-allowed',
              )}
            >
              {t('notifications_mark_all_read', 'Mark all read')}
            </button>
          </div>

          <div className="max-h-96 overflow-y-auto scrollbar-thin">
            {displayItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
                <Bell className="h-8 w-8 text-muted-foreground/40 mb-2" aria-hidden />
                <p className="text-sm font-medium text-foreground">{t('na_all_caught_up')}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('notifications_empty_body', 'New notifications will appear here.')}
                </p>
              </div>
            ) : (
              displayItems.map((notification) => {
                const rendered = Notifications.renderNotification(notification);
                const isUnread = !notification.readAt;
                return (
                  <button
                    key={notification.id}
                    type="button"
                    onClick={() => handleItemClick(notification)}
                    className={cn(
                      'w-full flex items-start gap-3 px-4 py-3 text-left transition-colors border-b border-border last:border-b-0',
                      isUnread ? 'bg-muted/60 hover:bg-muted' : 'hover:bg-accent/50',
                    )}
                  >
                    <span className="mt-0.5 shrink-0">{rendered.icon}</span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium text-foreground">
                        {rendered.title}
                      </span>
                      {rendered.body && (
                        <span className="block text-xs text-muted-foreground mt-0.5 line-clamp-2">
                          {rendered.body}
                        </span>
                      )}
                      <span className="block text-[11px] text-muted-foreground/70 mt-1">
                        {formatNotificationDateTime(notification.createdAt)}
                      </span>
                    </span>
                    {isUnread && (
                      <span
                        className="mt-1.5 h-2 w-2 rounded-full bg-foreground shrink-0"
                        aria-label={t('notifications_unread_badge', 'Unread')}
                      />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};
